import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { faArrowRotateForward, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faEnvelopes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack } from "@mui/material";
import type { GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { paginationModel, usePagination } from "api/hooks/usePagination";
import {
  useGetClinicPartnerInvitations,
  usePostResendClinicPartnerInvitation,
} from "api/services/clinic_partner/hooks";
import { ButtonContainer } from "routes/admin-billing/components/SharedStyles";
import { DateFilter } from "routes/invitations/components/DateFilter";
import { type ResendState, StatusIcon } from "routes/invitations/components/InvitationTable";
import { useDateFilter } from "routes/invitations/components/useDateFilter";
import { MUISelect } from "shared/atoms/MUI";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { useLocalizedDate } from "utils/date";

import { CustomToolbar } from "./CustomToolbar";
import type { MemberValue } from "./useFilterOptions";
import { ALL_MEMBERS, useFilterOptions } from "./useFilterOptions";

export const ClinicPartnerInvitationTable: React.FC = () => {
  const { t } = useTranslation();
  const { format, formats } = useLocalizedDate();
  const { pagination, setPagination } = usePagination();
  const { clinicPartnersMembersOptions } = useFilterOptions();
  const [selectedClinicPartnerMember, setSelectedClinicPartnerMember] = useState<MemberValue>(ALL_MEMBERS);
  const [resendState, setResendState] = useState<ResendState | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data, isLoading, refetch } = useGetClinicPartnerInvitations({
    params: {
      clinic_partner_ids: selectedClinicPartnerMember !== ALL_MEMBERS ? selectedClinicPartnerMember : undefined,
      limit: pagination.limit,
      page: pagination.page + 1,
    },
  });

  const postResendClinicPartnerInvitation = usePostResendClinicPartnerInvitation();

  const allRows = data?.items || [];
  const metadata = data?.metadata;
  const [rowCountState, setRowCountState] = useState(metadata?.total_count || 0);

  const { dateFilter, handleFilterChange, filteredRows: rows } = useDateFilter(allRows);

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      metadata?.total_count !== undefined ? metadata.total_count : prevRowCountState
    );
  }, [metadata, setRowCountState]);

  const onResendInvitation = async (id: number) => {
    setResendState({ id, status: "pending" });
    try {
      await postResendClinicPartnerInvitation.mutateAsync(id, {
        onError: () => {
          setResendState({ id, status: "error" });
          refetch();
        },
        onSuccess: () => {
          setResendState({ id, status: "success" });
          refetch();
        },
        onSettled: () => {
          setTimeout(() => {
            setResendState(null);
          }, 2000);
        },
      });
    } catch (error) {
      setResendState({ id, status: "error" });
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "id", headerName: t("invitations.table.id"), flex: 1, minWidth: 155, maxWidth: 300 },
      { field: "external_id", headerName: t("patients.external_id"), flex: 1, minWidth: 155, maxWidth: 300 },
      {
        field: "patient_name",
        headerName: t("invitations.table.name"),
        flex: 1,
        minWidth: 155,
        maxWidth: 300,
      },
      {
        field: "sent_at",
        headerName: t("invitations.table.sent_at"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), formats.LONG_LOCALIZED_DATE_PP) : ""),
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
      },
      {
        field: "last_reminder_at",
        headerName: t("invitations.table.last_reminder_at"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), formats.LONG_LOCALIZED_DATE_PP) : ""),
        flex: 1,
        minWidth: 188,
        maxWidth: 300,
      },
      {
        field: "_resend_invitation",
        headerName: t("invitations.table.resend_invitation"),
        renderCell: value => (
          <ButtonContainer
            onClick={() => {
              onResendInvitation(value.row.id);
            }}
            style={{ justifyContent: "start" }}
          >
            {resendState?.id === value.row.id && resendState?.status ? (
              <StatusIcon status={resendState.status} />
            ) : (
              <FontAwesomeIcon icon={faArrowRotateForward} style={{ height: "18px", width: "18px" }} />
            )}
          </ButtonContainer>
        ),
        disableColumnMenu: true,
        sortable: false,
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
      },
    ];
  }, [resendState]);

  return (
    <WidgetBox
      icon={<FontAwesomeIcon icon={faEnvelopes} />}
      title={t("invitations.table.title")}
      description={t("invitations.table.subheading")}
      color="blue"
      notifications={metadata?.total_count}
      toolbar={
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <DateFilter value={dateFilter} onChange={handleFilterChange} />
          <MUISelect
            size="small"
            options={clinicPartnersMembersOptions}
            disabled={isLoading}
            value={selectedClinicPartnerMember}
            onChange={event => {
              const value = event.target.value as MemberValue;
              setSelectedClinicPartnerMember(value);
              setPagination(prev => ({ ...prev, page: 0 }));
            }}
          />
        </Stack>
      }
    >
      <MUIDataGrid
        columns={columns}
        rows={rows}
        loading={isLoading}
        rowHeight={46}
        getRowId={row => row.id}
        initialState={{ pagination: { paginationModel } }}
        paginationMode="server"
        onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
        rowCount={rowCountState}
        rowSelection={false}
        slots={{
          noRowsOverlay: () => <EmptyResultOverlay text={t("invitations.table.no_rows")} icon={faUser} />,
          noResultsOverlay: () => <EmptyResultOverlay />,
          toolbar: () => (
            <CustomToolbar
              csvOptions={{ allColumns: true, fileName: `invitations_${format(new Date(), "yyyyMMdd_HHmmss")}` }}
            />
          ),
        }}
        localeText={{
          noRowsLabel: "",
          noResultsOverlayLabel: "",
        }}
        filterModel={filterModel}
        onFilterModelChange={model => setFilterModel(model)}
        disableColumnResize
        transparentBackground
        sx={{ marginTop: "-16px" }}
      />
    </WidgetBox>
  );
};
